import { Mail, Phone, Globe } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getTravelerName } from '../utils/formatters';
import type { Contact, Traveler } from '../utils/types';

interface ContactCardProps {
  contact: Contact;
  travelers: Traveler[];
  className?: string;
}

export function ContactCard({ contact, travelers, className }: ContactCardProps) {
  const isEmail = contact.contactType === 'Email';
  const travelerName = contact.travelerIds?.[0]
    ? getTravelerName(travelers, contact.travelerIds[0])
    : null;

  return (
    <div
      className={cn(
        'rounded-lg border border-slate-200 bg-white p-3 md:p-4 shadow-sm',
        'hover:shadow-md transition-shadow duration-200',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 md:gap-3">
          <div className={cn(
            'w-8 h-8 md:w-9 md:h-9 rounded-lg flex items-center justify-center shrink-0',
            isEmail ? 'bg-blue-50' : 'bg-green-50'
          )}>
            {isEmail ? (
              <Mail className="h-4 w-4 text-blue-500" />
            ) : (
              <Phone className="h-4 w-4 text-green-500" />
            )}
          </div>
          <div>
            <span className="text-xs text-slate-400 font-mono">{contact.id}</span>
            <div className="font-medium text-slate-900">
              {isEmail ? contact.address : contact.number}
            </div>
          </div>
        </div>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap items-center gap-2 mt-3">
        {contact.category && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
            {contact.category}
          </span>
        )}
        {contact.contactType && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
            {contact.contactType}
          </span>
        )}
        {contact.deviceType && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
            {contact.deviceType}
          </span>
        )}
        {contact.purpose && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
            {contact.purpose}
          </span>
        )}
        {contact.lang && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
            <Globe className="h-3 w-3" />
            {contact.lang}
          </span>
        )}
      </div>

      {/* Additional info */}
      {(contact.freeFlowText || contact.countryPhoneExtension || travelerName) && (
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5 text-sm">
          {contact.countryPhoneExtension && (
            <div className="flex items-center gap-2 text-slate-500">
              <span className="text-xs text-slate-400">Ext:</span>
              <span className="font-mono">{contact.countryPhoneExtension}</span>
            </div>
          )}
          {contact.freeFlowText && contact.freeFlowText !== contact.number && (
            <div className="text-slate-500 font-mono text-xs">
              {contact.freeFlowText}
            </div>
          )}
          {travelerName && (
            <div className="flex items-center gap-2 text-slate-500">
              <span className="text-xs text-slate-400">Traveler:</span>
              <span>{travelerName}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
